/**
 * Web Bluetooth transport for BLE label printers (e.g. Gprinter AS240).
 *
 * A browser cannot open a raw socket or USB print channel, but Chrome on
 * Android can talk to a BLE printer over GATT. We connect, find the first
 * writable characteristic, and stream raw TSPL bytes to it in small chunks
 * (BLE writes are capped by the negotiated MTU, and cheap printers have a
 * small receive buffer, so we pace the writes).
 *
 * Only works on a Chrome-family browser over https (or localhost), on a
 * printer that advertises BLE. Classic-Bluetooth-only printers won't appear
 * in the chooser.
 */

// Candidate GATT services to request access to. Web Bluetooth only lets you
// read services you declared up front, so we list every UUID cheap thermal
// printers are known to expose and then probe for a writable characteristic.
const CANDIDATE_SERVICES = [
  0xff00, 0xff01, 0xffe0, 0xffe5, 0xfee7, 0xfff0, 0x18f0,
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // ISSC / Microchip transparent UART
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART
  "000018f0-0000-1000-8000-00805f9b34fb",
];

const REMEMBER_KEY = "btPrinterName";
// Pacing for the fire-and-forget write path. 180 bytes every 15ms is the
// configuration that printed long batches reliably; it was later halved to 8ms,
// which overran the printer and silently dropped labels. Do not lower it.
const CHUNK_SIZE = 180;
const DEFAULT_CHUNK_DELAY_MS = 15;
const DELAY_KEY = "btPrinterChunkDelayMs";

// The safe delay is printer- and phone-specific, and each redeploy to the floor
// is slow, so it can be tuned on the spot: open the label page with
// ?btDelay=25 (remembered per device afterwards).
export function chunkDelayMs() {
  try {
    // The app is hash-routed, so ?btDelay= lands inside the hash
    // (#/path?btDelay=25) and never reaches window.location.search.
    const hashQuery = window.location.hash.split("?")[1] || "";
    const q =
      new URLSearchParams(hashQuery).get("btDelay") ||
      new URLSearchParams(window.location.search).get("btDelay");
    if (q !== null && q !== "" && Number(q) >= 0) {
      localStorage.setItem(DELAY_KEY, String(Number(q)));
      return Number(q);
    }
    const saved = localStorage.getItem(DELAY_KEY);
    if (saved !== null && Number(saved) >= 0) {
      return Number(saved);
    }
  } catch (e) {
    // No storage / no URL — fall through to the default.
  }
  return DEFAULT_CHUNK_DELAY_MS;
}

let device = null;
let characteristic = null;
// What the last successful connect found on the printer, for the on-screen log.
let discovered = [];
// Bumped by every connect attempt. Only the newest one may publish
// `characteristic` — see connectAndDiscover().
let attemptSeq = 0;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// True once this phone has picked a printer — i.e. it is a Bluetooth station.
// The label page uses it to decide whether opening the browser print dialog on
// arrival is helpful or just something to dismiss.
export function hasRememberedPrinter() {
  try {
    return !!localStorage.getItem(REMEMBER_KEY);
  } catch (e) {
    return false;
  }
}

export function isSupported() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

export function isConnected() {
  return !!(device && device.gatt && device.gatt.connected && characteristic);
}

// Walk every discovered service and return the first characteristic that can
// be written to. Also returns a text dump of what was found, so an
// unsupported printer can be diagnosed from the failure message.
async function findWritableCharacteristic(server) {
  const services = await server.getPrimaryServices();
  const seen = [];
  let writable = null;
  // Walk everything, even after a writable characteristic is found. Whether
  // this printer exposes a notify/read channel decides whether the transport
  // can ask it "are you finished?" (TSPL <ESC>!? / ~!A) instead of guessing
  // with timers — and that answer was previously thrown away.
  for (const service of services) {
    let chars = [];
    try {
      chars = await service.getCharacteristics();
    } catch (e) {
      continue;
    }
    for (const c of chars) {
      const p = c.properties;
      const flags =
        (p.write ? " write" : "") +
        (p.writeWithoutResponse ? " writeNR" : "") +
        (p.notify ? " NOTIFY" : "") +
        (p.indicate ? " indicate" : "") +
        (p.read ? " read" : "");
      seen.push(`${service.uuid.slice(0, 8)}/${c.uuid.slice(0, 8)}${flags}`);
      if (!writable && (p.write || p.writeWithoutResponse)) {
        writable = c;
      }
    }
  }
  return { characteristic: writable, map: seen };
}

// Show the Chrome device chooser and remember what the user picked, so we
// can reconnect to the same printer later without prompting again.
async function promptForDevice() {
  const chosen = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: CANDIDATE_SERVICES,
  });
  try {
    localStorage.setItem(REMEMBER_KEY, chosen.name || "");
  } catch (e) {
    // localStorage unavailable — we just won't remember across reloads.
  }
  return chosen;
}

// How long to wait for a silent reconnect before giving up and showing the
// chooser. Kept short so we fall back while the click's user activation is
// still valid (requestDevice needs a fresh gesture, ~5s window).
const RECONNECT_TIMEOUT_MS = 2500;

// Race a promise against a timeout so a hung BLE connect (an asleep printer
// never rejects on its own) can't block the fallback to the chooser.
function raceTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("Reconnect timed out")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// A device we can try to reconnect to silently (no chooser): one we already
// hold in memory, or one the browser granted in a past session.
async function knownDevice() {
  if (device) {
    return device;
  }
  if (navigator.bluetooth.getDevices) {
    try {
      const known = await navigator.bluetooth.getDevices();
      if (known && known.length) {
        let preferred = null;
        try {
          preferred = localStorage.getItem(REMEMBER_KEY);
        } catch (e) {
          preferred = null;
        }
        return known.find((d) => d.name === preferred) || known[0] || null;
      }
    } catch (e) {
      // getDevices unsupported/blocked — no silent reconnect available.
    }
  }
  return null;
}

function attachDisconnectHandler(dev) {
  dev.addEventListener("gattserverdisconnected", () => {
    characteristic = null;
  });
}

// Connect the GATT server and latch a writable characteristic. The caller
// bounds the whole thing with raceTimeout on the silent path — connect() AND
// discovery — so a slow discovery can't eat the user-activation window. An
// incompatible printer (no writable characteristic) is tagged so the caller
// can surface it instead of pointlessly opening the chooser.
async function connectAndDiscover() {
  const mine = ++attemptSeq;
  const server = await device.gatt.connect();
  const { characteristic: found, map } = await findWritableCharacteristic(
    server
  );

  // Disconnect the server THIS attempt opened, not whatever `device` points at
  // now — a superseded attempt must not tear down the live connection.
  const dropThisAttempt = () => {
    try {
      server.device.gatt.disconnect();
    } catch (e) {
      // ignore
    }
  };

  // A timed-out attempt keeps running (raceTimeout rejects the race, not the
  // promise it lost). If a newer attempt has started since, this one must not
  // publish its characteristic over the newer one's — and must not leave the
  // old printer held open, since BLE allows only one GATT connection at a time.
  if (mine !== attemptSeq) {
    dropThisAttempt();
    return;
  }

  if (!found) {
    dropThisAttempt();
    const err = new Error(
      "No writable characteristic found on this printer. Discovered: " +
        (map.join(", ") || "nothing")
    );
    err.unsupportedPrinter = true;
    throw err;
  }
  characteristic = found;
  discovered = map;
}

/**
 * Connect and latch a writable characteristic. The Chrome chooser appears
 * only the first time on a given phone; afterwards we try a fast silent
 * reconnect to the remembered printer. Crucially that silent attempt is
 * time-bounded — an asleep/unreachable printer would otherwise hang forever
 * and never fall through to the chooser (the "can't find printer" bug).
 */
export async function connect() {
  if (!isSupported()) {
    throw new Error(
      "Web Bluetooth not available — use Chrome on Android over https."
    );
  }
  if (isConnected()) {
    return device.name || "printer";
  }

  // 1) Fast, bounded silent reconnect to a known printer (no chooser). Bounded
  //    so an asleep printer can't hang us — we must still reach the chooser
  //    below while the click's user activation is valid.
  const candidate = await knownDevice();
  if (candidate) {
    if (device !== candidate) {
      device = candidate;
      attachDisconnectHandler(device);
    }
    try {
      // Bound connect AND discovery together, so a slow discovery can't eat
      // the user-activation window we need for the chooser fallback below.
      await raceTimeout(connectAndDiscover(), RECONNECT_TIMEOUT_MS);
      return device.name || "printer";
    } catch (e) {
      // An incompatible printer (no writable characteristic) won't be helped
      // by the chooser — the same error would just reappear. Surface it.
      if (e && e.unsupportedPrinter) {
        throw e;
      }
      // Asleep / out of range / held by another tab / timed out — cancel the
      // pending attempt, then show the chooser.
      try {
        device.gatt.disconnect();
      } catch (err) {
        // ignore
      }
    }
  }

  // 2) Chooser — still within the triggering click's user activation.
  device = await promptForDevice();
  attachDisconnectHandler(device);
  await connectAndDiscover();
  return device.name || "printer";
}

/**
 * Stream bytes to the connected printer.
 *
 * EVERY chunk is an acknowledged write. A label is ~36 KB, and
 * writeWithoutResponse has no flow control at all: the phone hands the whole
 * thing to the BLE stack as fast as it will take it, the printer's small
 * receive buffer overflows, and the overflow is dropped *silently* — the app
 * sees a clean send while the printer prints one label and discards the rest.
 * That is what "only prints 1" (and 2, and 5) actually was. An acked write
 * only resolves once the printer has taken the bytes, so the printer itself
 * sets the pace and cannot be overrun. It is slower, and worth it.
 */
// Enough newlines to move the printer's pipeline along, plus an acknowledged
// write so the phone's Bluetooth queue is genuinely drained rather than left
// sitting in the OS buffer.
async function sendPadding() {
  const pad = new Uint8Array(256).fill(10);
  if (characteristic.properties.writeWithoutResponse) {
    await characteristic.writeValueWithoutResponse(pad);
  }
  if (characteristic.properties.write) {
    await characteristic.writeValue(new Uint8Array([13, 10]));
  }
}

export async function write(bytes, opts = {}) {
  if (!isConnected()) {
    throw new Error("Printer not connected.");
  }
  const chunkSize = opts.chunkSize || CHUNK_SIZE;
  const delayMs = opts.delayMs != null ? opts.delayMs : chunkDelayMs();
  const canNoResponse =
    characteristic.properties.writeWithoutResponse &&
    typeof characteristic.writeValueWithoutResponse === "function";

  for (let i = 0; i < bytes.length; i += chunkSize) {
    // Checked between chunks, not between labels. A label is ~36KB and takes
    // seconds to push, so a Cancel that is only read once per label looks dead
    // — and if the printer has stopped accepting data it is never read at all.
    if (opts.shouldAbort && opts.shouldAbort()) {
      const abort = new Error("Cancelled");
      abort.cancelled = true;
      throw abort;
    }
    const chunk = bytes.slice(i, i + chunkSize);
    if (canNoResponse) {
      await characteristic.writeValueWithoutResponse(chunk);
    } else {
      await characteristic.writeValue(chunk);
    }
    if (delayMs) {
      await sleep(delayMs);
    }
  }

  // Push the job through. This printer runs one job behind: it holds the newest
  // one until more data arrives, so every label prints when the NEXT is sent and
  // the last of a batch never comes out — it appeared only when the page was
  // refreshed, because dropping the link flushed it. Padding is sent after each
  // label to give the real job a successor. Newlines carry no meaning in TSPL
  // between commands, so this cannot alter what prints.
  try {
    await sendPadding();
  } catch (e) {
    // Best-effort; the job itself is already sent.
  }
}

// Reported in the on-screen status so the pacing actually in use is visible
// from the floor — the difference between a working batch and a silently
// dropped one is a few milliseconds, and there is no other way to see it.
export function writeMode() {
  if (!characteristic) {
    return "none";
  }
  const mode = characteristic.properties.writeWithoutResponse
    ? "fast"
    : "acked";
  return `${mode} ${chunkDelayMs()}ms`;
}

// Every service/characteristic found on the printer, with its properties.
// Shown in the on-screen log so the printer's real capabilities — above all
// whether it can notify us of its status — are visible from the floor.
export function discoveredMap() {
  return discovered;
}

export function disconnect() {
  if (device && device.gatt && device.gatt.connected) {
    device.gatt.disconnect();
  }
  characteristic = null;
}

export default {
  isSupported,
  hasRememberedPrinter,
  isConnected,
  connect,
  write,
  writeMode,
  discoveredMap,
  disconnect,
};
